import { useState, useEffect, useRef, useCallback } from 'react'
import type { ConversationState, ConversationActions } from '../hooks/useConversation'
import { EMPTY_INCIDENT } from '../types/incident'
import type { IncidentForm, IncidentCategory } from '../types/incident'
import { useOfflineDictation } from '../hooks/useOfflineDictation'
import { updateIncident } from '../utils/incidentParser'
import { collapseRepeatedPhrases, mergeFinalSegments } from '../utils/textSanitize'
import { setDictationActive } from '../pwaUpdate'
import { BottomActionBar } from '../components/BottomActionBar'

interface Props {
  positions: any
  conv: ConversationState & ConversationActions
  onConfirm: () => void
  // Ekran żyje przez cały czas trwania incydentu (E2↔E3 nie odmontowuje go) —
  // `isActive` mówi, czy TO jest aktualnie widoczny krok (`screen === 2` w App),
  // żeby Auto-Submit hands-free odpalił się dopiero gdy user faktycznie go widzi.
  isActive: boolean
}

// Pole "Ważność" celowo usunięte z UI — słowo określające pilność (np. "pilnie",
// "natychmiast") i tak trafia naturalnie do treści poleceń/opisu (np. "potrzebny
// serwis pilnie"), więc osobne pole tylko duplikowało tę samą informację.
const LABELS: Record<Exclude<keyof IncidentForm, 'polecenia_arr' | 'kategoria' | 'waznosc'>, string> = {
  lokalizacja: 'Lokalizacja',
  obiekt: 'Obiekt / maszyna',
  typ: 'Typ zdarzenia',
  opis: 'Opis zdarzenia',
  polecenia: 'Polecenia / zlecenia',
  ppe: 'PPE / środki ochrony',
}

const FIELDS: Exclude<keyof IncidentForm, 'polecenia_arr' | 'kategoria' | 'waznosc'>[] = ['lokalizacja', 'obiekt', 'typ', 'opis', 'polecenia', 'ppe']

const CATEGORIES: IncidentCategory[] = ['Awaria', 'BHP', 'Magazyn']

// Hands-free (specyfikacja: "bez dotykania ekranu telefonu"): tyle czasu widać
// formularz jako wizualny feedback, zanim Auto-Submit sam zasymuluje zielony
// przycisk zatwierdzenia.
const AUTO_SUBMIT_DELAY_MS = 1500

const MicIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="22" />
    <line x1="8" y1="22" x2="16" y2="22" />
  </svg>
)

function startFieldDictation(onText: (text: string) => void, onFinal: (text: string) => void) {
  const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
  if (!SR) { onFinal(''); return null }
  const rec = new SR()
  let lastFull = ''
  rec.lang = 'pl-PL'; rec.continuous = true; rec.interimResults = true
  rec.onresult = (e: any) => {
    const finals: string[] = []
    let interim = ''
    for (let i = 0; i < e.results.length; i++) {
      const t = e.results[i][0].transcript
      if (e.results[i].isFinal) { finals.push(t) }
      else { interim = t }
    }
    const full = mergeFinalSegments(finals)
    lastFull = full
    onText(full + (interim ? ' ' + interim : ''))
  }
  rec.onerror = () => { onFinal(collapseRepeatedPhrases(lastFull)) }
  rec.onend = () => { onFinal(collapseRepeatedPhrases(lastFull)) }
  try { rec.start() } catch (_) { onFinal('') }
  return rec
}

export function IncidentFormScreen({ positions, conv, onConfirm, isActive }: Props) {
  const [form, setForm] = useState<IncidentForm>(conv.incidentForm ?? EMPTY_INCIDENT)
  const [activeField, setActiveField] = useState<keyof IncidentForm | null>(null)
  const [liveText, setLiveText] = useState('')
  const [dictating, setDictating] = useState(false)
  const fieldRecRef = useRef<any>(null)

  useEffect(() => { conv.setIncidentForm(form) }, [form])

  // ── Auto-Submit hands-free (hardware PTT) ───────────────────────────────
  // Zgłoszenie nagrane fizycznym PTT nie może czekać na ręczny tap „Zatwierdź"
  // — to złamałoby wymóg "bez dotykania ekranu telefonu". Formularz pokazuje
  // się na `AUTO_SUBMIT_DELAY_MS` (wizualny feedback), po czym sam symuluje
  // kliknięcie zielonego przycisku. Ref na `onConfirm` (zmienia tożsamość co
  // render App — nie może być zależnością efektu, bo timer nigdy by nie doleciał
  // do końca) — dokładnie ten sam wzorzec co `onPressRef`/`onReleaseRef` w `usePttManager`.
  const onConfirmRef = useRef(onConfirm)
  onConfirmRef.current = onConfirm
  useEffect(() => {
    if (!conv.isHardwarePtt || !isActive) return
    const t = setTimeout(() => { onConfirmRef.current() }, AUTO_SUBMIT_DELAY_MS)
    return () => clearTimeout(t)
  }, [conv.isHardwarePtt, isActive])

  // `useState` powyżej czyta conv.incidentForm TYLKO RAZ, przy montowaniu. Jeśli
  // ten ekran nie zostanie w pełni odmontowany między dwoma kolejnymi incydentami
  // (React może zostawić tę samą instancję, jeśli sciezka renderowania na to
  // pozwoli), pola pokazywały dane z POPRZEDNIEGO incydentu, mimo że conv.incidentForm
  // miał już nowe, poprawnie wyekstrahowane wartości (np. "polecenia"). Ten efekt
  // domyka pętlę w drugą stronę — bez ryzyka nieskończonej pętli, bo druga runda
  // przekazuje identyczną referencję, więc React ubija zbędny render.
  useEffect(() => {
    if (conv.incidentForm) setForm(conv.incidentForm)
  }, [conv.incidentForm])

  // Bezpiecznik auto-aktualizacji PWA: nie przeładuj strony w trakcie dyktowania pola/całości.
  // Cleanup przy odmontowaniu jest konieczny — bez niego zejście z ekranu w trakcie
  // dyktowania zostawiało globalną flagę "true" na zawsze i PWA nigdy więcej się nie aktualizowało.
  useEffect(() => {
    setDictationActive(dictating || activeField !== null)
    return () => setDictationActive(false)
  }, [dictating, activeField])

  const dictation = useOfflineDictation({
    onSegment: (t) => { setLiveText(t) },
    onDone: (full) => {
      setForm(prev => updateIncident(prev, full))
      setDictating(false)
      setLiveText('')
    },
    onError: () => { setDictating(false) },
  })

  const toggleGlobalDictation = () => {
    if (dictating) { dictation.stop(); return }
    setDictating(true); setLiveText(''); dictation.start()
  }

  const startField = useCallback((field: keyof IncidentForm) => {
    if (fieldRecRef.current) { try { fieldRecRef.current.stop() } catch (_) {} }
    setActiveField(field); setLiveText('')
    fieldRecRef.current = startFieldDictation(
      (t) => { setLiveText(t) },
      (t) => {
        setLiveText('')
        setForm(prev => {
          if (field === 'polecenia' && prev[field]) {
            return { ...prev, [field]: `${prev[field]}\n${t}` }
          }
          return { ...prev, [field]: t }
        })
        setActiveField(null)
        fieldRecRef.current = null
      }
    )
  }, [])

  const handleFieldTap = (field: keyof IncidentForm) => {
    if (activeField === field && fieldRecRef.current) {
      try { fieldRecRef.current.stop() } catch (_) {}
      return
    }
    startField(field)
  }

  return (
    <div className="relative w-full h-full bg-[#0a0a0f]">
      <div style={{
        position: 'absolute', top: `${positions.titleTop}%`, left: 0, right: 0,
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        color: '#D9D9D9', textAlign: 'center',
        fontFamily: '"Inter", sans-serif', fontSize: 'clamp(24px, 4vw, 32px)',
        fontWeight: 200, letterSpacing: '3px',
        fontFeatureSettings: "'liga' off, 'clig' off", whiteSpace: 'nowrap',
      }}>Raport z incydentu</div>

      <div style={{
        position: 'absolute', top: `${positions.convTop}%`,
        left: '4.8%', right: '4.8%', height: `${positions.convHeightPct}%`,
        borderRadius: 33, overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.05)',
        boxShadow: 'inset 1px 1px 2px rgba(255,255,255,0.15)',
        backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
        background: 'linear-gradient(180deg, rgba(0, 255, 85, 0.08) 4.81%, rgba(0, 255, 85, 0.21) 56.33%, rgba(0, 255, 85, 0.08) 100%)',
      }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 28, background: 'linear-gradient(to bottom, rgba(0,0,0,0.30) 0%, transparent 100%)', pointerEvents: 'none', zIndex: 10 }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 10, background: 'linear-gradient(to top, rgba(0,0,0,0.12) 0%, transparent 100%)', pointerEvents: 'none', zIndex: 10 }} />
        <div className="no-scrollbar" style={{ padding: '28px 22px', height: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 4 }}>
            {CATEGORIES.map(cat => {
              const selected = form.kategoria === cat
              return (
                <div
                  key={cat}
                  onClick={() => setForm(prev => ({ ...prev, kategoria: cat }))}
                  style={{
                    padding: '5px 12px',
                    borderRadius: 999,
                    fontSize: 12,
                    cursor: 'pointer',
                    border: `1px solid ${selected ? 'rgba(0,255,85,0.6)' : 'rgba(255,255,255,0.15)'}`,
                    background: selected ? 'rgba(0,255,85,0.18)' : 'rgba(0,0,0,0.20)',
                    color: selected ? '#86efac' : 'rgba(255,255,255,0.5)',
                    transition: 'all 150ms ease',
                  }}
                >
                  {cat}
                </div>
              )
            })}
          </div>

          {FIELDS.map(field => {
            const isActive = activeField === field
            const value = isActive ? (liveText || form[field] || '') : form[field]
            const maxH = (field === 'opis' || field === 'polecenia') ? (isActive ? 126 : 86) : (isActive ? 78 : 48)
            return (
              <div
                key={field}
                onClick={() => handleFieldTap(field)}
                style={{
                  border: `1px solid ${isActive ? 'rgba(0,255,0,0.5)' : 'rgba(255,255,255,0.15)'}`,
                  borderRadius: 10, padding: '8px 12px 6px 12px', background: 'rgba(0,0,0,0.20)',
                  cursor: 'pointer',
                  minHeight: maxH,
                  maxHeight: maxH,
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 200ms ease',
                }}
              >
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, marginBottom: 1 }}>
                  {LABELS[field]}{isActive && ' - słucham...'}
                </div>
                <div style={{ position: 'relative', maxHeight: maxH - 22, overflow: 'hidden' }}>
                  <div style={{
                    color: value ? '#fff' : 'rgba(255,255,255,0.35)', fontSize: 15, lineHeight: 1.3,
                    whiteSpace: 'pre-line', wordBreak: 'break-word', minHeight: maxH - 28,
                  }}>
                    {value || <MicIcon />}
                  </div>
                  <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0, height: 8, pointerEvents: 'none',
                    background: 'linear-gradient(to top, rgba(0,0,0,0.35) 0%, transparent 100%)',
                  }} />
                </div>
              </div>
            )
          })}

          {dictating && (
            <div style={{ color: '#0f0', fontSize: 13, marginTop: 8 }}>
              {liveText || 'Słucham...'}
            </div>
          )}
        </div>
      </div>

      <div style={{
        position: 'absolute', bottom: `${positions.actionBarBottom}%`,
        left: 0, right: 0, display: 'flex', justifyContent: 'center',
        transformOrigin: 'center bottom',
      }}>
        <BottomActionBar
          onConfirmTap={onConfirm}
          onMicTap={toggleGlobalDictation}
          isRecording={dictating}
        />
      </div>
    </div>
  )
}
